import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Filtro colapsado que se despliega al hacer hover (desktop) o click (mobile).
 * Muestra solo la etiqueta y valor actual; al pasar el mouse, muestra las opciones.
 * En mobile, usa click para abrir/cerrar el dropdown.
 */
/**
 * @param {Object} props
 * @param {Array<{value: string, label: string}>} [props.options]
 * @param {Array<{label: string, onClick: () => void}>} [props.customOptions] - Opciones con handler custom (ej. "Personalizado" que abre modal)
 * @param {boolean} [props.mobileOnly] - Si true, muestra el dropdown clickeable en mobile
 */
export default function CollapsibleFilterGroup({ label, value, options = [], onChange, customOptions = [], getOptionLabel }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const currentLabel = getOptionLabel ? getOptionLabel(value) : options.find((o) => o.value === value)?.label ?? value;

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (!dropdownRef.current?.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleOptionClick = (optValue) => {
    onChange(optValue);
    setIsOpen(false);
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  return (
    <div ref={dropdownRef} className="relative flex-shrink-0">
      {/* Desktop hover + Mobile click button */}
      <button
        type="button"
        onClick={handleButtonClick}
        className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition bg-white/10 text-white/70 hover:bg-white/20 border min-w-[5rem] justify-between touch-manipulation active:scale-95
          ${isOpen
            ? 'bg-white/15 border-white/20 text-white'
            : 'border-transparent group-hover:border-white/20 group-hover:bg-white/20'
          }
        `}
      >
        <span className="text-white/70">{label}:</span>
        <span className="truncate max-w-[100px] sm:max-w-[7rem]">{currentLabel}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 flex-shrink-0 text-white/50 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown menu - visible on click or hover */}
      <div
        className={`absolute left-0 top-full pt-1 -mt-1 z-[9100] transition-all duration-150 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="bg-slate-800/95 backdrop-blur-xl border border-white/20 rounded-xl shadow-xl py-2 min-w-[max(10rem,90vw)] sm:min-w-[10rem] max-w-[calc(100vw-2rem)]">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleOptionClick(opt.value)}
              className={`w-full px-4 py-2 text-left text-xs font-medium transition hover:bg-white/10 active:scale-95 touch-manipulation ${
                value === opt.value ? 'bg-white/15 text-white' : 'text-white/70'
              }`}
            >
              {opt.label}
            </button>
          ))}
          {customOptions.map((opt, i) => (
            <button
              key={`custom-${i}`}
              type="button"
              onClick={() => {
                opt.onClick();
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-xs font-medium transition hover:bg-white/10 text-white/70 border-t border-white/10 mt-1 pt-2 active:scale-95 touch-manipulation"
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
